import Papa from 'papaparse';

import api from '@/api';
import {
  ASSET_ID_COLUMN,
  EtisArrestExportRow,
  EtisColumn,
} from '@/api/caseTable/EtisArrestsExport';
import {
  EtisArrestImportRowResult,
  EtisArrestImportRowResultType,
} from '@/api/caseTable/EtisArrestsImport';

/**
 * Amount of arrests imported in a single request.
 */
const PAGE_SIZE = 500;

/**
 * Results of importing ETIS import files, and the error that stopped the
 * import, if any.
 */
export type EtisArrestsImportResult = {
  results: EtisArrestImportRowResult[];
  error: unknown | null;
};

/**
 * Called with the current page and the results of all rows known so far,
 * first before the first page is sent and then after each imported page.
 */
export type EtisArrestsImportProgress = (
  page: number,
  pages: number,
  results: EtisArrestImportRowResult[]
) => void;

/**
 * Columns and rows of ETIS import file, with all values kept as text.
 */
type EtisFile = {
  columns: EtisColumn[];
  rows: Record<string, string>[];
};

/**
 * Reads ETIS import file into its columns and rows. The first two lines of
 * the file are headers: the first one with column groups and the second one
 * with column names.
 */
function parseEtisFile(lines: string[][]): EtisFile {
  const [groups = [], names = [], ...values] = lines;

  return {
    columns: names.map((name, i) => ({ group: groups[i] ?? '', name })),
    rows: values.map((line) =>
      names.reduce(
        (row: Record<string, string>, name, i) => ({
          ...row,
          [name]: line[i] ?? '',
        }),
        {}
      )
    ),
  };
}

function createResult(
  varaId: string,
  result: EtisArrestImportRowResultType,
  message: string
): EtisArrestImportRowResult {
  return { varaId, kisAvalduseId: '', result, message };
}

/**
 * Pairs the rows of "Varad" and "Arestid" files by VaraId into arrests.
 *
 * Assets without an arrest (f.e payments or bank accounts that were never
 * arrested) are skipped. Arrests without an asset can not be imported.
 */
function pairEtisRows(
  assets: Record<string, string>[],
  arrests: Record<string, string>[]
): { pairs: EtisArrestExportRow[]; results: EtisArrestImportRowResult[] } {
  const arrestsByAssetId = new Map(
    arrests.map((arrest) => [arrest[ASSET_ID_COLUMN], arrest])
  );
  const pairs: EtisArrestExportRow[] = [];
  const results: EtisArrestImportRowResult[] = [];

  assets.forEach((asset) => {
    const assetId = asset[ASSET_ID_COLUMN];
    const arrest = arrestsByAssetId.get(assetId);

    if (!arrest) {
      // Vara does not link to a direct Arrest - so such Vara row is skipped.
      results.push(
        createResult(
          assetId,
          EtisArrestImportRowResultType.SKIPPED,
          'Vara ei ole arestitud.'
        )
      );
      return;
    }

    pairs.push({ vara: asset, arest: arrest });
    arrestsByAssetId.delete(assetId);
  });

  // All Arrest rows left-over did not have a pair in Varad file.
  // This is incorrect behaviour from ETIS export side and such rows are marked as failed.
  arrestsByAssetId.forEach((arrest, assetId) => {
    results.push(
      createResult(
        assetId,
        EtisArrestImportRowResultType.FAILED,
        'Aresti vara puudub Varad failist.'
      )
    );
  });

  return { pairs, results };
}

/**
 * Imports arrests from lines of ETIS import files, sending them to back-end
 * in pages one by one.
 */
async function importArrestsFromEtisLines(
  assetLines: string[][],
  arrestLines: string[][],
  progress: EtisArrestsImportProgress
): Promise<EtisArrestsImportResult> {
  const assets = parseEtisFile(assetLines);
  const arrests = parseEtisFile(arrestLines);
  const { pairs, results } = pairEtisRows(assets.rows, arrests.rows);
  const pages = Math.ceil(pairs.length / PAGE_SIZE);
  const imported: EtisArrestImportRowResult[] = [];

  // Rows decided in front-end are known before anything is imported.
  progress(0, pages, [...results]);

  try {
    for (let page = 1; page <= pages; page++) {
      imported.push(
        ...(await api.caseTable.importEtisArrests({
          // Columns are validated by back-end.
          columns: { vara: assets.columns, arest: arrests.columns },
          rows: pairs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
        }))
      );

      progress(page, pages, [...imported, ...results]);
    }
  } catch (error) {
    // Importing is stopped on the first failed request, but the results of
    // the pages imported before it are still returned.
    return { results: [...imported, ...results], error };
  }

  return { results: [...imported, ...results], error: null };
}

/**
 * Reads lines of a csv file, with all values kept as text, so that f.e
 * leading zeros of arrest ids are not lost.
 */
function readCsvLines(file: File): Promise<string[][]> {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      delimiter: ';',
      skipEmptyLines: true,
      complete: ({ data }: { data: string[][] }) => resolve(data),
      error: (err) => reject(new Error('Viga faili lugemisel. ' + err)),
    });
  });
}

/**
 * Imports active arrests from ETIS import files "Varad" and "Arestid" into
 * cases of the current bureau. Arrests are sent to back-end in pages one by
 * one, and a progress function passed can be used to display the current
 * progress compared to all pages, and the results of the rows so far.
 *
 * Importing the same files again is safe, since arrests already active in
 * a case are skipped.
 *
 * @returns Result of each row of the files imported, and the error of the
 *   request that stopped the import, if any.
 */
export async function importArrestsFromEtisCsv(
  assetsFile: File,
  arrestsFile: File,
  progress: EtisArrestsImportProgress
): Promise<EtisArrestsImportResult> {
  const [assetLines, arrestLines] = await Promise.all([
    readCsvLines(assetsFile),
    readCsvLines(arrestsFile),
  ]);

  return importArrestsFromEtisLines(assetLines, arrestLines, progress);
}
